// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@awc/App/Store/Reducers";
import { xIsPLPTableView } from "@awc/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { productListCategoryFiltersStyles } from "@insite/content-library/Widgets/ProductList/ProductListCategoryFilters";
import ProductListFiltersAccordionSection, {
    ProductListFilterAccordionSectionStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    // XNG-Changes
    // store getProductListDataView(state) into productData
    const productData = getProductListDataView(state);
    return {
        loaded: !!productData.value,
        stockedItemsOnly: state.pages.productList.productFilters.stockedItemsOnly,
        displayFacetsForStockedItems: getSettingsCollection(state).productSettings.displayFacetsForStockedItems,
        // XNG-Changes
        showTableLayout: xIsPLPTableView(state),
    };
};

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListStockedItemsFilterStyles {
    // XNG-Changes
    checkboxStyles?: ProductListFilterAccordionSectionStyles;
}

export const productListStockedItemsFilterStyles: ProductListStockedItemsFilterStyles = {};

const styles = productListStockedItemsFilterStyles;

const ProductListStockedItemsFilter: FC<Props> = ({
    stockedItemsOnly,
    loaded,
    addProductFilters,
    removeProductFilters,
    displayFacetsForStockedItems,
    // XNG-Changes
    showTableLayout,
}) => {
    if (!loaded || !displayFacetsForStockedItems) {
        return null;
    }

    const onChangeFacet = (facet: FacetModel) => {
        if (facet.selected) {
            removeProductFilters({ stockedItemsOnly: false });
        } else {
            addProductFilters({ stockedItemsOnly: true });
        }
    };

    // XNG-Changes: change name and count.
    const facet = [
        {
            id: "stocked",
            name: translate("Show In-Stock Only"),
            count: -1,
            selected: stockedItemsOnly ?? false,
        },
    ];

    // XNG-Changes: return Checbox for table layout
    return showTableLayout ? (
        <>
            {facet?.map(facet => (
                <CheckboxGroup key={facet.id} {...styles.checkboxStyles?.checkBoxGroup}>
                    <Checkbox
                        {...(facet.selected
                            ? styles.checkboxStyles?.checkBoxSelected
                            : styles.checkboxStyles?.checkBox)}
                        checked={facet.selected}
                        onChange={() => onChangeFacet(facet)}
                        data-test-selector={`facetFilter${facet.id}`}
                    >
                        {facet.name}
                    </Checkbox>
                </CheckboxGroup>
            ))}
        </>
    ) : (
        <ProductListFiltersAccordionSection
            // XNG-Changes: change title to match design.
            title={translate("Stock Availability")}
            facets={facet}
            onChangeFacet={onChangeFacet}
            showMoreLimit={1}
            extendedStyles={productListCategoryFiltersStyles}
            expandByDefault={true}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListStockedItemsFilter),
    definition: {
        group: "Product List",
        displayName: "Stocked Items Filter",
        // XNG-Changes: add CategoryDetailsPage
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
